import { useState, useEffect } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import BannerImage from '../assets/nearby-merchants/banner-1.png';
import { RiShoppingBag4Fill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/user';
import { useCart } from '../contexts/cart';
import log from '../utils/logger';
import { getApiErrorMessage } from '../utils/apiError';
import api from '../api/api';
import useToast from '../hooks/useToast';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import Toast from './Toast';

const CartModal = ({ isCartModalOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  useBodyScrollLock(isCartModalOpen);
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [editingQuantities, setEditingQuantities] = useState({});

  const { idToken, user, logout, adminImpersonate } = useUser();
  const { cartItems, totalItems, totalPrice, setCart, updateQuantity, clearCart, setCartId } = useCart();
  const { toastMessage, toastType, showToast } = useToast();

  useEffect(() => {
    if (isCartModalOpen) {
      fetchCart();
      setTimeout(() => setShow(true), 10);
    } else {
      setShow(false);
    }
  }, [isCartModalOpen]);

  const fetchCart = async () => {
    try {
      if ((!user || !idToken) && !adminImpersonate) {
        return;
      }
      const res = await api.get('/buyer/cart/getCart', {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        params: {
          adminImpersonateToken: adminImpersonate || null, buyerEmail: user?.email, buyerId: user?.id,
        },
      });
      const { cartId, cartItems } = res.data.response.data;
      setCartId(cartId || null);
      setCart(cartItems || []);
    } catch (err) {
      log.error('Failed to fetch cart:', err);
      clearCart();
      const message = err.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
      } else {
        showToast(getApiErrorMessage(err, t), 'error');
      }
    }
  };

  const syncCartToBackend = async () => {
    try {
      const items = cartItems.map(item => ({
        productId: item.productId,
        variantId: item.variantId, // optional
        quantity: item.quantity,
        merchantConfigId: item.merchantConfigId,
      }));

      await api.put(
        '/buyer/cart/updateCart',
        {
          cartItems: items,
          adminImpersonateToken: adminImpersonate || null, buyerEmail: user?.email, buyerId: user?.id,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      );

      setHasUnsyncedChanges(false);
    } catch (error) {
      log.error('Failed to sync cart:', error);
      const message = error.response?.data?.error || '';
      if (message === 'TOKEN_EXPIRED' || message === 'INVALID_ID_TOKEN') {
        navigate('/home');
        sessionStorage.setItem('sessionExpired', 'true');
        sessionStorage.setItem(
          'toast',
          JSON.stringify({
            message: t('sessionExpiredMessage'),
            type: 'error',
          }),
        );
        logout();
        clearCart();
      } else {
        showToast(getApiErrorMessage(error, t), 'error');
      }
    }
  };

  const handleQuantityChange = async (item, quantityChange) => {
    const minQty = item.minQuantity || 1;
    const maxQty = item.maxQuantity || Infinity;
    const newQuantity = item.quantity + quantityChange;

    if (isNaN(newQuantity)) {
      log.error('Quantity or change is not a valid number');
      updateQuantity(item.productId, item.merchantConfigId, item.variantId, 0);
      setHasUnsyncedChanges(true);
      return;
    }

    if (newQuantity > maxQty) {
      showLimitMessage(t('maxQuantityMessage', { qty: maxQty }), 'warning');
      return;
    }

    if (newQuantity < minQty) {
      updateQuantity(item.productId, item.merchantConfigId, item.variantId, 0);
    } else {
      updateQuantity(item.productId, item.merchantConfigId, item.variantId, newQuantity);
    }

    setHasUnsyncedChanges(true);
  };

  const showLimitMessage = (msg) => {
    setLimitMessage(msg);
    setTimeout(() => setLimitMessage(''), 3000);
  };

  const handleCheckout = async () => {
    try {
      if (hasUnsyncedChanges) {
        await syncCartToBackend();
      }

      navigate('/checkout');
    } catch (err) {
      log.error('❌ Could not create order:', err);
    }
  };

  const handleModalClose = async () => {
    if (hasUnsyncedChanges) {
      await syncCartToBackend();
    }
    onClose();
  };

  const getItemKey = (item) =>
    `${item.productId}-${item.variantId || 'default'}`;

  const handleQuantityInput = (item, value) => {
    // Allow empty while editing
    if (value === '') {
      setEditingQuantities((prev) => ({
        ...prev,
        [getItemKey(item)]: '',
      }));
      return;
    }

    // Only allow digits
    if (!/^\d+$/.test(value)) {
      return;
    }

    setEditingQuantities((prev) => ({
      ...prev,
      [getItemKey(item)]: value,
    }));
  };

  const handleQuantityBlur = (item) => {
    const key = getItemKey(item);

    const rawValue =
      editingQuantities[key] !== undefined
        ? editingQuantities[key]
        : item.quantity;

    let quantity = Number(rawValue);

    const min = item.minQuantity || 1;
    const max = item.maxQuantity || Infinity;

    if (Number.isNaN(quantity)) {
      quantity = 0;
    }

    quantity = Math.min(max, quantity);

    updateQuantity(
      item.productId,
      item.merchantConfigId,
      item.variantId,
      quantity < min ? 0 : quantity,
    );

    setEditingQuantities((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });

    setHasUnsyncedChanges(true);
  };

  const handleQuantityKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  return (
    <div>
      <div className={`fixed bottom-0 lg:top-0 right-0 left-auto w-full lg:w-[550px] h-fit lg:h-full max-h-[85dvh] lg:max-h-full overflow-hidden bg-white z-50 transition-transform duration-300 ${show ? 'modalanimatedRight' : 'whennotmodalcloseRight'}`}>
        {/* Close Icon */}
        <div className='flex justify-between sticky top-0 items-center w-full py-3 px-4 border-b border-black border-opacity-10'>
          <button className='w-9 h-9 flex items-center justify-center rounded-lg bg-[#f3f3f4]' onClick={handleModalClose}>
            <IoCloseSharp />
          </button>
          <h2 className='font-semibold text-lg text-[#000000DE]'>{t('cart')}</h2>
          <h2></h2>
        </div>

        {/* Product List */}
        <div className='h-full relative'>
          <ul className='px-4 pt-6 pb-[60px] lg:pb-[120px] flex flex-col gap-3 overflow-y-auto h-full max-h-[60dvh] lg:max-h-dvh min-h-[200px] lg:min-h-dvh boxscrollbar2'>
            {cartItems.length === 0 ? (
              <div className='text-center text-sm text-gray-400'>{t('Your cart is empty')}</div>
            ) : (
              cartItems.map((item) => (
                <li key={item.productId + (item.variantId || '')} className='flex items-center gap-2 w-full'>
                  <img src={item.images?.[0] || BannerImage} alt="Product" className='w-14 h-14 object-cover rounded-lg' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium leading-[1.375] mb-1 truncate'>
                      {i18n.language === 'en' ? item.enName : item.frName}{item.enVariantName && item.frVariantName ? ` - ${i18n.language === 'en' ? item.enVariantName : item.frVariantName}` : ''}
                    </p>
                    <p className='text-xs font-semibold leading-[1.2]'>{item.price.toFixed(2)}$</p>
                  </div>
                  <div className='flex-shrink-0 border border-[#E6E6E6] rounded flex items-center h-8'>
                    <button className='px-2 font-semibold text-sm' onClick={() => handleQuantityChange(item, -1)} disabled={item.quantity <= 0}>-</button>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={
                        editingQuantities[getItemKey(item)] ?? item.quantity
                      }
                      onChange={(e) => handleQuantityInput(item, e.target.value)}
                      onBlur={() => handleQuantityBlur(item)}
                      onKeyDown={(e) => handleQuantityKeyDown(e)}
                      className="w-10 text-center font-semibold text-sm outline-none bg-transparent"
                    />
                    <button className='px-2 font-semibold text-sm' onClick={() => handleQuantityChange(item, 1)}>+</button>
                  </div>
                </li>
              ))
            )}
          </ul>

          {/* Checkout Button */}
          {totalItems > 0 && (
            <div className='fixed bottom-0 w-full lg:w-[550px] left-0 lg:left-auto lg:right-0 bg-white pt-3 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]'>
              <div onClick={handleCheckout} className='min-h-[60px] bg-[#0b1223] p-2 rounded-lg flex items-center justify-between gap-2 transition-all duration-300 hover:bg-opacity-90 cursor-pointer'>
                <ul className='flex pl-3'>
                  <li className='pr-2 border-r-2 border-white font-semibold text-sm text-white leading-5'>
                    {totalItems} {t('items')}
                  </li>
                  <li className='pl-2 font-semibold text-sm text-white leading-5'>
                    {totalPrice.toFixed(2)}$
                  </li>
                </ul>
                <button className='flex items-center gap-2 px-2 text-xs uppercase text-white font-semibold'>
                  <RiShoppingBag4Fill className='text-lg' />
                  {t('Checkout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-dvh bg-[#212121] bg-opacity-[0.46] z-40 transition-opacity duration-300 ${
          show ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleModalClose}
      />

      {limitMessage && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-[#1B1223AA] text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm font-medium">
          {limitMessage}
        </div>
      )}
      <Toast message={toastMessage} type={toastType} />
    </div>
  );
};

export default CartModal;
